import { Injectable } from '@angular/core';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { ActionsService } from '@core/actions-modals/actions.service';
import { AuthService } from '@features/auth/auth.service';
import { DeviationStatusesEnum } from '@features/project/deviation/statuses.enum';
import { ToastStatusEnum } from '@ui-kit/app-toast/toast-status.enum';
import { ToasterService } from '@ui-kit/app-toast/toaster.service';
import { handleError } from '@ui-kit/utility/handleHttpErrorResponse';
import { CollectionModel, CrudService, DeleteEntityModel } from 'collection';
import { of, Subject } from 'rxjs';
import { catchError, filter, map, mergeMap, take } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class DeviationActionsService {
  isAdmin: boolean;

  deviationBatchDelete: DeleteEntityModel = this._crud.createDeleteManyEntity({ name: 'deviations/batch-delete' });

  isUserAdminOrClosed(deviationData: any, isAdmin: boolean): boolean {
    if (isAdmin) {
      return true;
    } else if (deviationData?.status === DeviationStatusesEnum.Open) {
      // if user not admin and project closed editing is forbidden
      return false;
    }
  }

  isDeleteAvailable(deviationData): boolean {
    const currentUser = this._auth.currentUser;

    if (this.isAdmin) {
      return true;
    }

    return deviationData.status !== DeviationStatusesEnum.Closed && deviationData.user_id === currentUser.id;
  }

  canDeleteDeviationImage(deviationData): boolean {
    const currentUser = this._auth.currentUser;

    if (this.isAdmin) {
      return true;
    }

    return (
      deviationData.status !== DeviationStatusesEnum.Closed &&
      (deviationData.user_id === currentUser.id || deviationData.assigned_user_id === currentUser.id)
    );
  }

  isEditAvailable(deviationData, isCreateMode?): boolean {
    const currentUser = this._auth.currentUser;

    if (this.isAdmin || isCreateMode) {
      return true;
    }

    if (deviationData.status === DeviationStatusesEnum.Closed) {
      return false;
    }

    return deviationData.user_id == currentUser.id || deviationData.assigned_user_id === currentUser.id;
  }

  deleteMany({ ids, collection }: { ids: Array<number>; collection: CollectionModel }): Subject<boolean> {
    const confirmedSubject = new Subject<boolean>();
    const titlePlural = marker('Are you sure you want to delete these deviations?');
    const title = marker('Are you sure you want to delete this deviation?');
    const config = {
      title: collection.selectedItems.size > 1 ? titlePlural : title,
      description: marker('You are not able to undo this action'),
      confirmButtonText: marker('Yes, I confirm')
    };

    const successMessage = {
      type: ToastStatusEnum.Success,
      message: marker('Changes are successfully saved')
    };

    this._actionsService
      .confirm(config)
      .pipe(
        take(1),
        filter(({ confirmed }) => confirmed),
        mergeMap(({ modal }) => confirmedAction$(modal))
      )
      .subscribe();

    const confirmedAction$ = (modal) =>
      of(modal).pipe(
        mergeMap(() =>
          this.deviationBatchDelete
            .delete({
              params: { ids: ids.join(',') }
            })
            .pipe(
              map(() => confirmedSubject.next(true)),
              map(() => this._toastService.show(successMessage)),
              catchError(({ error }) => handleError(error, this._toastService)),
              map(() => modal.close())
            )
        )
      );

    return confirmedSubject;
  }

  constructor(
    private _auth: AuthService,
    private _crud: CrudService,
    private _actionsService: ActionsService,
    private _toastService: ToasterService
  ) {
    this.isAdmin = this._auth.isAdmin();
  }
}
